import {all, put, takeLatest, takeLeading} from 'redux-saga/effects';
import {actions, ActionType} from './action';
import {getCommentList} from './apiGet';
import {
  callApiGetComments,
  callApiGetReplyComments,
  callApiPostComment,
  callApiChangeLikeStatus,
  callApiDeleteComment,
  callApiReportComment,
  callApiBlockUser,
} from './callApi';
import {pToast} from '../../common/container/PToast';
import {selectT} from '../../common/i18n';
import {actions as commonActions} from '../../common/state/action';
import {getIsEnd, getTotalCount} from '../../common/state/apiGet';
import {IPagingParam, makeFetchSaga} from '../../common/util/fetch';
import {typedSelect} from '../../common/util/saga';

function* fetchEpisodeComments(
  action: ReturnType<typeof actions.fetchEpisodeComments>,
  param: IPagingParam
) {
  const {episodeId, sort} = action.payload;
  const {success, data} = yield callApiGetComments({
    relationType: 'EPISODE',
    relationId: episodeId,
    sort,
    ...param,
  });

  if (success) {
    const t = yield* selectT();
    yield put(
      actions.addEpisodeComments({
        comments: getCommentList({t, comments: data}),
        isFirst: action.isFirst,
        sort,
      })
    );
  }
}

function* fetchEpisodeReplyComments(
  action: ReturnType<typeof actions.fetchEpisodeReplyComments>,
  param: IPagingParam
) {
  const {parentId} = action.payload;
  const {success, data, meta} = yield callApiGetReplyComments({
    parentId,
    limit: param.limit,
    cursor: param.cursor,
  });

  if (success) {
    const t = yield* selectT();
    yield put(
      actions.addEpisodeReplyComments({
        parentId,
        replyComments: getCommentList({t, comments: data}),
        isFirst: action.isFirst,
        isEnd: getIsEnd(meta),
        replyTotalCount: getTotalCount(meta),
      })
    );
  }
}

function* fetchEpisodeLikeStatus(
  action: ReturnType<typeof actions.fetchEpisodeLikeStatus>
) {
  const {commentId, likeStatus} = action.payload;
  const prevLikeStatus = yield* typedSelect(
    ({comment}) => comment.episodeCommentList.commentMap[commentId].likeStatus
  );
  yield put(actions.setEpisodeLikeStatus(commentId, likeStatus));
  const {success} = yield callApiChangeLikeStatus(commentId, likeStatus);

  if (!success) {
    yield put(actions.setEpisodeLikeStatus(commentId, prevLikeStatus));
  }
}

function* fetchDeleteEpisodeComment(
  action: ReturnType<typeof actions.fetchDeleteEpisodeComment>
) {
  const {episodeId, commentId} = action.payload;
  const {success} = yield callApiDeleteComment(commentId);

  if (success) {
    yield put(actions.deleteEpisodeComment(commentId));
    yield put(
      commonActions.decreaseTotalCount(
        ActionType.FetchEpisodeComments,
        episodeId
      )
    );
  }
}

function* fetchPostEpisodeComment(
  action: ReturnType<typeof actions.fetchPostEpisodeComment>
) {
  const {contentId, episodeId, isSpoiler, content} = action.payload;
  const success = yield* callApiPostComment({
    contentId,
    relationId: episodeId,
    relationType: 'EPISODE',
    parentCommentId: 0,
    spoiling: isSpoiler,
    text: content,
  });

  if (success) {
    yield put(
      actions.fetchEpisodeComments(
        {
          episodeId,
          sort: 'createdDt',
        },
        true
      )
    );
  }
}

function* fetchPostEpisodeReplyComment(
  action: ReturnType<typeof actions.fetchPostEpisodeReplyComment>
) {
  const {parentId, content, contentId, episodeId} = action.payload;
  const success = yield* callApiPostComment({
    contentId,
    relationId: episodeId,
    relationType: 'EPISODE',
    parentCommentId: parentId,
    spoiling: false,
    text: content,
  });

  if (success) {
    yield put(actions.fetchEpisodeReplyComments(parentId, true));
  }
}

function* fetchContentComments(
  action: ReturnType<typeof actions.fetchContentComments>,
  param: IPagingParam
) {
  const {contentId, sort} = action.payload;
  const {success, data} = yield callApiGetComments({
    relationType: 'CONTENT',
    relationId: contentId,
    sort,
    ...param,
  });

  if (success) {
    const t = yield* selectT();
    yield put(
      actions.addContentComments({
        contentId,
        comments: getCommentList({
          t,
          comments: data,
          isIncludeEpisodeTitle: true,
        }),
        isFirst: !param.cursor,
        sort,
      })
    );
  }
}

function* fetchContentReplyComments(
  action: ReturnType<typeof actions.fetchContentReplyComments>,
  param: IPagingParam
) {
  const {contentId, parentId} = action.payload;
  const {success, data, meta} = yield callApiGetReplyComments({
    parentId,
    ...param,
  });

  if (success) {
    const t = yield* selectT();
    yield put(
      actions.addContentReplyComments({
        contentId,
        parentId,
        replyComments: getCommentList({t, comments: data}),
        isFirst: !param.cursor,
        isEnd: getIsEnd(meta),
        replyTotalCount: getTotalCount(meta),
      })
    );
  }
}

function* fetchContentLikeStatus(
  action: ReturnType<typeof actions.fetchContentLikeStatus>
) {
  const {contentId, commentId, likeStatus} = action.payload;
  const prevLikeStatus = yield* typedSelect(
    ({comment}) =>
      comment.contentCommentListMap[contentId].commentMap[commentId].likeStatus
  );
  yield put(actions.setContentLikeStatus({contentId, commentId, likeStatus}));
  const {success} = yield callApiChangeLikeStatus(commentId, likeStatus);

  if (!success) {
    yield put(actions.setEpisodeLikeStatus(commentId, prevLikeStatus));
  }
}

function* fetchDeleteContentComment(
  action: ReturnType<typeof actions.fetchDeleteContentComment>
) {
  const {contentId, commentId} = action.payload;
  const {success} = yield callApiDeleteComment(commentId);

  if (success) {
    yield put(actions.deleteContentComment(contentId, commentId));
    yield put(
      commonActions.decreaseTotalCount(
        ActionType.FetchContentComments,
        contentId
      )
    );
  }
}

function* fetchReportComment(
  action: ReturnType<typeof actions.fetchReportComment>
) {
  const {onSuccess, ...rest} = action.payload;
  const {success, data} = yield callApiReportComment(rest);

  if (success) {
    onSuccess(data?.url);
  }
}

function* fetchEventComments(
  action: ReturnType<typeof actions.fetchEventComments>,
  param: IPagingParam
) {
  const {eventId, sort} = action.payload;
  const {success, data} = yield callApiGetComments({
    relationType: 'EVENT',
    relationId: eventId,
    sort,
    ...param,
  });

  if (success) {
    const t = yield* selectT();
    yield put(
      actions.addEventComments({
        comments: getCommentList({t, comments: data}),
        isFirst: action.isFirst,
        sort,
      })
    );
  }
}

function* fetchEventReplyComments(
  action: ReturnType<typeof actions.fetchEventReplyComments>,
  param: IPagingParam
) {
  const {parentId} = action.payload;
  const {success, data, meta} = yield callApiGetReplyComments({
    parentId,
    limit: param.limit,
    cursor: param.cursor,
  });

  if (success) {
    const t = yield* selectT();
    yield put(
      actions.addEventReplyComments({
        parentId,
        replyComments: getCommentList({t, comments: data}),
        isFirst: action.isFirst,
        isEnd: getIsEnd(meta),
        replyTotalCount: getTotalCount(meta),
      })
    );
  }
}

function* fetchEventLikeStatus(
  action: ReturnType<typeof actions.fetchEventLikeStatus>
) {
  const {commentId, likeStatus} = action.payload;
  const prevLikeStatus = yield* typedSelect(
    ({comment}) => comment.eventCommentList.commentMap[commentId].likeStatus
  );
  yield put(actions.setEventLikeStatus(commentId, likeStatus));
  const {success} = yield callApiChangeLikeStatus(commentId, likeStatus);

  if (!success) {
    yield put(actions.setEventLikeStatus(commentId, prevLikeStatus));
  }
}

function* fetchDeleteEventComment(
  action: ReturnType<typeof actions.fetchDeleteEventComment>
) {
  const {eventId, commentId} = action.payload;
  const {success} = yield callApiDeleteComment(commentId);

  if (success) {
    yield put(actions.deleteEventComment(commentId));
    yield put(
      commonActions.decreaseTotalCount(ActionType.FetchEventComments, eventId)
    );
  }
}

function* fetchPostEventComment(
  action: ReturnType<typeof actions.fetchPostEventComment>
) {
  const {eventId, isSpoiler, content} = action.payload;
  const success = yield* callApiPostComment({
    relationId: eventId,
    relationType: 'EVENT',
    parentCommentId: 0,
    spoiling: isSpoiler,
    text: content,
  });

  if (success) {
    yield put(
      actions.fetchEventComments(
        {
          eventId,
          sort: 'createdDt',
        },
        true
      )
    );
  }
}

function* fetchPostEventReplyComment(
  action: ReturnType<typeof actions.fetchPostEventReplyComment>
) {
  const {parentId, content, eventId} = action.payload;
  const success = yield* callApiPostComment({
    relationId: eventId,
    relationType: 'EVENT',
    parentCommentId: parentId,
    spoiling: false,
    text: content,
  });

  if (success) {
    yield put(actions.fetchEventReplyComments(parentId, true));
  }
}

function* fetchBlockUser(action: ReturnType<typeof actions.fetchBlockWriter>) {
  const {commentId, contentId} = action.payload;
  const {success, data} = yield callApiBlockUser({
    commentId,
    isBlock: true,
  });

  if (success) {
    const {userId} = data;
    yield put(actions.setBlockComment(userId, true, contentId));
    const t = yield* selectT();
    pToast(t('comment_block_toast'));
  }
}

function* fetchUnBlockUser(
  action: ReturnType<typeof actions.fetchUnBlockWriter>
) {
  const {commentId, contentId} = action.payload;
  const {success, data} = yield callApiBlockUser({
    commentId,
    isBlock: false,
  });

  if (success) {
    const {userId} = data;
    yield put(actions.setBlockComment(userId, false, contentId));
    const t = yield* selectT();
    pToast(t('comment_unblock_toast'));
  }
}

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchEpisodeComments,
      makeFetchSaga({fetchSaga: fetchEpisodeComments, canCache: false})
    ),
    takeLatest(
      ActionType.FetchEpisodeReplyComments,
      makeFetchSaga({fetchSaga: fetchEpisodeReplyComments, canCache: false})
    ),
    takeLeading(
      ActionType.FetchEpisodeLikeStatus,
      makeFetchSaga({fetchSaga: fetchEpisodeLikeStatus, canCache: false})
    ),
    takeLeading(
      ActionType.FetchDeleteEpisodeComment,
      makeFetchSaga({fetchSaga: fetchDeleteEpisodeComment, canCache: false})
    ),
    takeLeading(
      ActionType.FetchPostEpisodeComment,
      makeFetchSaga({fetchSaga: fetchPostEpisodeComment, canCache: false})
    ),
    takeLeading(
      ActionType.FetchPostEpisodeReplyComment,
      makeFetchSaga({
        fetchSaga: fetchPostEpisodeReplyComment,
        canCache: false,
      })
    ),

    takeLatest(
      ActionType.FetchContentComments,
      makeFetchSaga({fetchSaga: fetchContentComments, canCache: false})
    ),
    takeLatest(
      ActionType.FetchContentReplyComments,
      makeFetchSaga({fetchSaga: fetchContentReplyComments, canCache: false})
    ),
    takeLeading(
      ActionType.FetchContentLikeStatus,
      makeFetchSaga({fetchSaga: fetchContentLikeStatus, canCache: false})
    ),
    takeLeading(
      ActionType.FetchDeleteContentComment,
      makeFetchSaga({fetchSaga: fetchDeleteContentComment, canCache: false})
    ),

    takeLatest(
      ActionType.FetchEventComments,
      makeFetchSaga({fetchSaga: fetchEventComments, canCache: false})
    ),
    takeLatest(
      ActionType.FetchEventReplyComments,
      makeFetchSaga({fetchSaga: fetchEventReplyComments, canCache: false})
    ),
    takeLeading(
      ActionType.FetchEventLikeStatus,
      makeFetchSaga({fetchSaga: fetchEventLikeStatus, canCache: false})
    ),
    takeLeading(
      ActionType.FetchDeleteEventComment,
      makeFetchSaga({fetchSaga: fetchDeleteEventComment, canCache: false})
    ),
    takeLeading(
      ActionType.FetchPostEventComment,
      makeFetchSaga({fetchSaga: fetchPostEventComment, canCache: false})
    ),
    takeLeading(
      ActionType.FetchPostEventReplyComment,
      makeFetchSaga({
        fetchSaga: fetchPostEventReplyComment,
        canCache: false,
      })
    ),

    takeLeading(
      ActionType.FetchReportComment,
      makeFetchSaga({fetchSaga: fetchReportComment, canCache: false})
    ),
    takeLeading(
      ActionType.FetchBlockWriter,
      makeFetchSaga({fetchSaga: fetchBlockUser, canCache: false})
    ),
    takeLeading(
      ActionType.FetchUnBlockWriter,
      makeFetchSaga({fetchSaga: fetchUnBlockUser, canCache: false})
    ),
  ]);
}
